import type { TDocumentDefinitions, Content, ContentTable, TableCell, Node as PdfNode, NodeQueries } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import {
  UnifiedInspectionIntegrityReportMapper,
  type IntegrityReportDocumentModel
} from "../../../application/mappers/unified-inspection-integrity-report-mapper.js";
import type { GeneratedLineList } from "../../../domain/inspection-book/line-list.js";
import { buildRiLineListTable } from "./line-list-render.js";
import { NOT_INFORMED } from "../../../shared/format/report-value.js";

/**
 * REPORT DEFINITION: RI_OFFICIAL_V1
 * Modelo Oficial e Padrão de Relatório de Inspeção de Integridade do INSPECT IA.
 * Mapeamento estrutural e visual 100% fiel aos dados reais da inspeção (sem mocks nem hardcoded).
 */
export class RiBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const rawData = snapshot.data || {};

    // Normalize into the unified DocumentModel (Single Source of Truth)
    let model: IntegrityReportDocumentModel;
    if (rawData.report && rawData.equipment && rawData.equipment.equipmentTypeDisplay) {
      model = rawData as IntegrityReportDocumentModel;
    } else {
      model = UnifiedInspectionIntegrityReportMapper.mapToDocumentModel({
        report: rawData.report || { id: snapshot.id, inspectionId: rawData.inspection?.id } as any,
        inspection: rawData.inspection,
        equipmentSnapshot: rawData.equipment,
        checklistComponents: rawData.checklistComponents,
        performedInspectionMethods: rawData.performedMethods || rawData.ndtReports || rawData.report?.ndts || rawData.ndts,
        recommendations: rawData.recommendations,
        photos: rawData.photos,
        signatories: rawData.signatories,
        client: { name: rawData.clientName },
        /**
         * Estas três chaves existiam no mapper e NÃO eram repassadas: um
         * snapshot cru (o caminho normal quando o RI não chega pré-mapeado)
         * perdia silenciosamente a seção LINHAS QUE COMPOE O SISTEMA e a de
         * DISPOSITIVOS DE SEGURANÇA E INSTRUMENTOS — o mapper devolvia
         * `null`/`[]` e `addSection` omitia o bloco sem erro nenhum.
         */
        generatedLineList: rawData.generatedLineList,
        safetyDevices: rawData.safetyDevices,
        instruments: rawData.instruments
      });
    }

    const report = model?.report || ({} as any);
    const equipment = model?.equipment || ({} as any);
    const inspection = model?.inspection || ({} as any);
    const checklistComponents = model?.checklistComponents || [];
    const performedMethods = model?.performedMethods || [];
    const recommendations = model?.recommendations || [];
    const photos = model?.photos || [];
    const signatories = model?.signatories || [];

    return {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [36, 135, 36, 45],
      header: (currentPage: number, pageCount: number) => {
        return this.buildDynamicFullHeader(report, equipment, model, currentPage, pageCount);
      },
      footer: (currentPage: number, pageCount: number) => {
        return {
          margin: [36, 15, 36, 0],
          stack: [
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 0.5, lineColor: '#CBD5E1' }],
              margin: [0, 0, 0, 5]
            },
            {
              columns: [
                { text: 'INSPECT-AI | Relatório de Inspeção de Integridade', style: 'docFooterText' },
                { text: `Página ${currentPage} de ${pageCount}`, style: 'docFooterText', alignment: 'right' }
              ]
            }
          ]
        };
      },
      pageBreakBefore: (currentNode: PdfNode, nodeQueries: NodeQueries) => {
        // PREVENÇÃO DE TÍTULOS ÓRFÃOS:
        // Se o nó for um cabeçalho de seção e não houver conteúdo subsequente na mesma página, quebra a página.
        if (currentNode.id === 'sectionBanner' && nodeQueries.getFollowingNodesOnPage().length === 0) {
          return true;
        }
        return false;
      },
      content: this.buildContentArray(model),
      styles: {
        docHeaderBrand: { fontSize: 13, bold: true, color: '#111827' },
        docHeaderDocTitle: { fontSize: 12, bold: true, color: '#111827', alignment: 'right' },
        docHeaderSubtitle: { fontSize: 7.5, color: '#4B5563' },
        docFooterText: { fontSize: 7.5, color: '#4B5563' },
        
        bannerNumberBox: { fontSize: 8.5, bold: true, color: '#FFFFFF', fillColor: '#111827', alignment: 'center' },
        bannerTitleBox: { fontSize: 8.5, bold: true, color: '#111827', fillColor: '#F3F4F6' },
        
        subBannerText: { fontSize: 7.5, bold: true, color: '#111827', fillColor: '#E5E7EB', margin: [4, 2, 4, 2] },

        tblHeaderCell: { fontSize: 7.5, bold: true, color: '#FFFFFF', fillColor: '#374151', alignment: 'center', margin: [2, 3, 2, 3] },
        tableHeader: { fontSize: 7.5, bold: true, color: '#FFFFFF', fillColor: '#374151', alignment: 'center', margin: [2, 3, 2, 3] },
        tableBody: { fontSize: 7.5, color: '#111827', margin: [2, 2.5, 2, 2.5] },
        tblLabelCell: { fontSize: 7.5, bold: true, color: '#111827', fillColor: '#F9FAFB', margin: [3, 2.5, 3, 2.5] },
        tblValueCell: { fontSize: 7.5, color: '#374151', margin: [3, 2.5, 3, 2.5] },
        tblDataCell: { fontSize: 7.5, color: '#111827', margin: [3, 2.5, 3, 2.5] },
        tblDataCellCenter: { fontSize: 7.5, color: '#111827', alignment: 'center', margin: [3, 2.5, 3, 2.5] },

        bodyTextBlock: { fontSize: 8, color: '#111827', lineHeight: 1.25, margin: [4, 4, 4, 4] },
        docLegalNotice: { fontSize: 8, color: '#374151', lineHeight: 1.2 },
        docNoticeFootnote: { fontSize: 7, color: '#6B7280', italics: true },

        photoBoxHeader: { fontSize: 7.5, bold: true, color: '#111827', fillColor: '#F3F4F6', margin: [4, 2, 4, 2] },
        photoFieldLabel: { fontSize: 7.5, bold: true, color: '#111827' },
        photoFieldValue: { fontSize: 7.5, color: '#374151' }
      },
      defaultStyle: {
        fontSize: 8,
        font: 'Roboto'
      }
    };
  }

  private formatDocStatus(status: string): string {
    if (!status) return 'EM ELABORAÇÃO';
    const s = status.toUpperCase();
    if (s === 'DRAFT' || s === 'IN_ELABORATION' || s === 'EM_ELABORACAO') return 'EM ELABORAÇÃO';
    if (s === 'CONCLUDED' || s === 'CONCLUIDO') return 'CONCLUÍDO';
    if (s === 'ISSUED' || s === 'EMITIDO') return 'EMITIDO';
    if (s === 'APPROVED' || s === 'ENGINEERING_APPROVED') return 'APROVADO';
    if (s === 'SIGNED' || s === 'ASSINADO') return 'ASSINADO';
    if (s === 'RETURNED' || s === 'DEVOLVIDO') return 'DEVOLVIDO';
    if (s === 'IN_CORRECTION') return 'EM CORREÇÃO';
    return status;
  }

  // -------------------------------------------------------------
  // VALIDAÇÃO DE CONTEÚDO E CONSTRUÇÃO DINÂMICA
  // -------------------------------------------------------------
  private hasMeaningfulContent(value: any): boolean {
    if (value === null || value === undefined) return false;
    
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === '' || v === '-' || v === '—' || v === 'n/a' || v === 'não aplicável' || v === 'nao aplicavel') return false;
      return true;
    }
    
    if (typeof value === 'number' || typeof value === 'boolean') {
      return true;
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) return false;
      return value.some(item => this.hasMeaningfulContent(item));
    }
    
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return false;
      return keys.some(k => this.hasMeaningfulContent(value[k]));
    }
    
    return true;
  }

  private buildContentArray(model: any): Content[] {
    const content: Content[] = [];
    const report = model?.report || ({} as any);
    const equipment = model?.equipment || ({} as any);
    const inspection = model?.inspection || ({} as any);
    const checklistComponents = model?.checklistComponents || [];
    const performedMethods = model?.performedMethods || [];
    const recommendations = model?.recommendations || [];
    const photos = model?.photos || [];
    const signatories = model?.signatories || [];
    const isPipingSystem = !!model?.generatedLineList;

    // 01 IDENTIFICAÇÃO
    content.push(this.buildSectionBanner('01', 'IDENTIFICAÇÃO'));
    content.push(this.buildIdentificationGrid(report, equipment, inspection, model.clientName));

    // 02 OBJETIVO
    content.push(this.buildSectionBanner('02', 'OBJETIVO'));
    content.push(this.buildSubHeaderBanner('Objetivo da inspeção'));
    content.push(this.buildObjectiveBlock(report.objective));

    // 03 DADOS DE PROJETO
    content.push(this.buildSectionBanner('03', 'DADOS DE PROJETO'));
    content.push(this.buildProjectDataGrid(equipment));

    // 04 DADOS DE OPERAÇÃO
    content.push(this.buildSectionBanner('04', 'DADOS DE OPERAÇÃO'));
    content.push(this.buildOperationDataGrid(equipment));

    // 05 DISPOSITIVOS DE SEGURANÇA E INSTRUMENTOS
    content.push(this.buildSectionBanner('05', 'DISPOSITIVOS DE SEGURANÇA E INSTRUMENTOS'));
    content.push(this.buildSafetyDevicesAndInstrumentsGrid(model.safetyDevices || [], model.instruments || []));

    // 06 DADOS DA INSPEÇÃO EXECUTADA
    content.push(this.buildSectionBanner('06', 'DADOS DA INSPEÇÃO EXECUTADA'));
    content.push(this.buildInspectionRealizedGrid(inspection));
    if (report.inspectionObservations && this.hasMeaningfulContent(report.inspectionObservations)) {
      content.push(this.buildSubHeaderBanner('Observações gerais da inspeção'));
      content.push(this.buildInspectionObservationsBlock(report.inspectionObservations));
    }

    // 07 INSPEÇÃO VISUAL POR COMPONENTE
    content.push(this.buildSectionBanner('07', 'INSPEÇÃO VISUAL POR COMPONENTE'));
    content.push(this.buildVisualInspectionTable(checklistComponents));

    // 08 ENSAIOS NÃO DESTRUTIVOS EXECUTADOS
    content.push(this.buildSectionBanner('08', 'ENSAIOS NÃO DESTRUTIVOS EXECUTADOS'));
    content.push(this.buildNdtTable(performedMethods));

    // 09 SERVIÇOS DE MANUTENÇÃO REALIZADOS
    content.push(this.buildSectionBanner('09', 'SERVIÇOS DE MANUTENÇÃO REALIZADOS'));
    content.push(this.buildMaintenanceTable(report.maintenanceServices));

    // 10 RECOMENDAÇÕES DE INSPEÇÃO
    content.push(this.buildSectionBanner('10', 'RECOMENDAÇÕES DE INSPEÇÃO'));
    content.push(this.buildRecommendationsTable(recommendations));

    if (isPipingSystem) {
      // 11 LINHAS QUE COMPÕE O SISTEMA (Sistema de Tubulação)
      content.push(this.buildSectionBanner('11', 'LINHAS QUE COMPOE O SISTEMA'));
      content.push(...this.buildLineListSection(model.generatedLineList!));

      // 12 DATAS LIMITES PARA PRÓXIMAS INSPEÇÕES
      content.push(this.buildSectionBanner('12', 'DATAS LIMITES PARA PRÓXIMAS INSPEÇÕES'));
      content.push(this.buildNextInspectionDatesTable(report.nextInspectionDates));

      // 13 DISCUSSÃO SOBRE MECANISMOS DE DANO
      content.push(this.buildSectionBanner('13', 'DISCUSSÃO SOBRE MECANISMOS DE DANO'));
      content.push(this.buildSubHeaderBanner('Mecanismos possíveis / ativos / históricos e discussão técnica'));
      content.push(this.buildDamageMechanismsBlock(report.damageMechanisms));

      // 14 CONCLUSÃO
      content.push(this.buildSectionBanner('14', 'CONCLUSÃO'));
      content.push(this.buildConclusionGrid(report));

      // 15 ANEXO FOTOGRÁFICO
      content.push(this.buildSectionBanner('15', 'ANEXO FOTOGRÁFICO'));
      if (photos && photos.length > 0) {
        content.push(this.buildSubHeaderBanner('Registro de evidências fotográficas vinculadas aos componentes e condições identificadas durante a inspeção.'));
      }
      content.push(...this.buildPhotosPages(photos));

      // 16 TERMO DE RESPONSABILIDADE & SIGNATÁRIOS
      // RI-VIS-01: Agrupar título, texto introdutório e tabela em stack unbreakable
      // para evitar quebra de página entre o cabeçalho da seção e a tabela de signatários.
      content.push({
        stack: [
          this.buildSectionBanner('16', 'TERMO DE RESPONSABILIDADE & SIGNATÁRIOS'),
          {
            text: 'Responsabilizam-se pelas informações aqui contidas, para os devidos efeitos, nos termos da NR-13 e da legislação aplicável dos sistemas CREA/CONFEA e CFT, os signatários:',
            style: 'docLegalNotice',
            margin: [0, 4, 0, 8]
          },
          ...this.buildSignatoriesTable(signatories),
          {
            text: 'Documento gerado a partir dos registros técnicos da inspeção. Assinatura física e carimbo requeridos para validação pelo cliente/fiscalização.',
            style: 'docNoticeFootnote',
            margin: [0, 10, 0, 0]
          }
        ],
        unbreakable: true
      });
    } else {
      // 11 DATAS LIMITES PARA PRÓXIMAS INSPEÇÕES
      content.push(this.buildSectionBanner('11', 'DATAS LIMITES PARA PRÓXIMAS INSPEÇÕES'));
      content.push(this.buildNextInspectionDatesTable(report.nextInspectionDates));

      // 12 DISCUSSÃO SOBRE MECANISMOS DE DANO
      content.push(this.buildSectionBanner('12', 'DISCUSSÃO SOBRE MECANISMOS DE DANO'));
      content.push(this.buildSubHeaderBanner('Mecanismos possíveis / ativos / históricos e discussão técnica'));
      content.push(this.buildDamageMechanismsBlock(report.damageMechanisms));

      // 13 CONCLUSÃO
      content.push(this.buildSectionBanner('13', 'CONCLUSÃO'));
      content.push(this.buildConclusionGrid(report));

      // 14 ANEXO FOTOGRÁFICO
      content.push(this.buildSectionBanner('14', 'ANEXO FOTOGRÁFICO'));
      if (photos && photos.length > 0) {
        content.push(this.buildSubHeaderBanner('Registro de evidências fotográficas vinculadas aos componentes e condições identificadas durante a inspeção.'));
      }
      content.push(...this.buildPhotosPages(photos));

      // 15 TERMO DE RESPONSABILIDADE & SIGNATÁRIOS
      // RI-VIS-01: Agrupar título, texto introdutório e tabela em stack unbreakable
      // para evitar quebra de página entre o cabeçalho da seção e a tabela de signatários.
      content.push({
        stack: [
          this.buildSectionBanner('15', 'TERMO DE RESPONSABILIDADE & SIGNATÁRIOS'),
          {
            text: 'Responsabilizam-se pelas informações aqui contidas, para os devidos efeitos, nos termos da NR-13 e da legislação aplicável dos sistemas CREA/CONFEA e CFT, os signatários:',
            style: 'docLegalNotice',
            margin: [0, 4, 0, 8]
          },
          ...this.buildSignatoriesTable(signatories),
          {
            text: 'Documento gerado a partir dos registros técnicos da inspeção. Assinatura física e carimbo requeridos para validação pelo cliente/fiscalização.',
            style: 'docNoticeFootnote',
            margin: [0, 10, 0, 0]
          }
        ],
        unbreakable: true
      });
    }

    return content;
  }

  // -------------------------------------------------------------
  // BARRA DE CONTROLE SUPERIOR (REPEATING HEADER)
  // -------------------------------------------------------------
  private buildDynamicFullHeader(report: any, equipment: any, model: any, currentPage: number = 1, pageCount: number = 1): Content {
    let modalityDisplay = '';
    const rawModality = model.inspection?.modality || model.inspection?.inspectionType || '';
    if (rawModality) {
      const modUpper = rawModality.toUpperCase();
      const modalityMapping: Record<string, string> = {
        'EXTERNAL': 'INSPEÇÃO EXTERNA',
        'INTERNAL': 'INSPEÇÃO INTERNA',
        'GENERAL': 'INSPEÇÃO GERAL',
        'EXTRAORDINARY': 'INSPEÇÃO EXTRAORDINÁRIA',
        'PERIODIC': 'INSPEÇÃO PERIÓDICA'
      };
      modalityDisplay = modalityMapping[modUpper] || 
        (modUpper.includes('INSPEÇÃO') || modUpper.includes('INSPECAO') ? modUpper : `INSPEÇÃO ${modUpper}`);
    } else {
      modalityDisplay = 'INSPEÇÃO';
    }

    const qrToken = report.verificationToken || report.id;
    const qrUrl = `https://app.inspect-ia.com.br/verify-report/${qrToken}`;

    // O mapeador grava o logo em `report.tenantLogo`; ler só `model.tenantLogo` deixava o logo em
    // imagem sempre vazio. Sem logo (o padrão), o texto INSPECT IA de sempre.
    const tenantLogo: string | null = model.tenantLogo || model.report?.tenantLogo || null;
    const logoContent: any = tenantLogo ? { image: tenantLogo, fit: [160, 45], margin: [0, 0, 0, 4] } : { text: 'INSPECT IA', fontSize: 24, bold: true, color: '#111827', margin: [0, 0, 0, 4] };

    const issueDateStr = report.issueDate ? new Date(report.issueDate).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR');

    const leftStack: any[] = [
      logoContent,
      { text: 'SISTEMA DE INSPEÇÃO E INTEGRIDADE MECÂNICA', fontSize: 6.5, color: '#6B7280', margin: [0, -2, 0, 6] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 240, y2: 0, lineWidth: 0.5, lineColor: '#D1D5DB' }], margin: [0, 0, 0, 6] },
      { text: 'RELATÓRIO DE INSPEÇÃO / INTEGRIDADE', fontSize: 10, bold: true, color: '#1F2937', margin: [0, 0, 0, 2] }
    ];

    const clientUnitStr = [
      model.clientName,
      (equipment.unitAreaName || equipment.unit)
    ].filter(Boolean).join(' - ').toUpperCase();

    if (clientUnitStr) {
      leftStack.push({ text: clientUnitStr, fontSize: 8, color: '#4B5563', bold: false, margin: [0, 0, 0, 2] });
    }

    const typeTagStr = `${equipment.tag} - ${modalityDisplay}`.toUpperCase();
    leftStack.push({ text: typeTagStr, fontSize: 9, color: '#1F2937', bold: true, margin: [0, 0, 0, 0] });

    return {
      table: {
        widths: ['45%', '15%', '40%'],
        body: [
          [
            // Left Block
            {
              stack: leftStack
            },
            // Center Block (QR Code)
            {
              stack: [
                { qr: qrUrl, fit: 75, alignment: 'center' }
              ],
              margin: [0, 5, 0, 0]
            },
            // Right Block (Metadata)
            {
              stack: [
                {
                  table: {
                    widths: ['45%', '55%'],
                    body: [
                      [{ text: 'Rel. cliente nº:', fontSize: 8, color: '#4B5563', margin: [8, 2.5, 0, 2.5] }, { text: report.clientReportNumber || '—', fontSize: 8, color: '#111827', margin: [0, 2.5, 0, 2.5] }],
                      [{ text: 'Rel. INSPECT IA nº:', fontSize: 8, color: '#4B5563', margin: [8, 2.5, 0, 2.5] }, { text: report.reportNumber || '—', fontSize: 8, color: '#111827', margin: [0, 2.5, 0, 2.5] }],
                      [{ text: 'Revisão:', fontSize: 8, color: '#4B5563', margin: [8, 2.5, 0, 2.5] }, { text: report.revision || '00', fontSize: 8, color: '#111827', margin: [0, 2.5, 0, 2.5] }],
                      /**
                       * PERÍODO DA INSPEÇÃO — quando o trabalho foi feito.
                       *
                       * Vem do Período Inicial/Final do próprio RI, os mesmos
                       * valores da seção DADOS DA INSPEÇÃO EXECUTADA. Entra
                       * ANTES da Data da emissão de propósito: sem ele, quem
                       * lia o cabeçalho tinha só a data de emissão e a tomava
                       * pela data da inspeção — que pode ser semanas depois.
                       */
                      [{ text: 'Período da inspeção:', fontSize: 8, color: '#4B5563', margin: [8, 2.5, 0, 2.5] }, { text: this.formatInspectionPeriod(model?.inspection), fontSize: 8, color: '#111827', margin: [0, 2.5, 0, 2.5] }],
                      [{ text: 'Data da emissão:', fontSize: 8, color: '#4B5563', margin: [8, 2.5, 0, 2.5] }, { text: issueDateStr, fontSize: 8, color: '#111827', margin: [0, 2.5, 0, 2.5] }],
                      [{ text: 'Ordem de serviço:', fontSize: 8, color: '#4B5563', margin: [8, 2.5, 0, 2.5] }, { text: report.osNumber || '—', fontSize: 8, color: '#111827', margin: [0, 2.5, 0, 2.5] }]
                    ]
                  },
                  layout: 'noBorders'
                }
              ],
              margin: [10, 10, 0, 0]
            }
          ]
        ]
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === node.table.body.length ? 0.5 : 0),
        vLineWidth: (i: number) => (i === 2 ? 0.5 : 0),
        hLineColor: () => '#9CA3AF',
        vLineColor: () => '#D1D5DB',
        paddingLeft: (i: number) => (i === 2 ? 12 : 0),
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 8
      },
      margin: [36, 28, 36, 0]
    };
  }

  // -------------------------------------------------------------
  // BANNERS DE SEÇÃO
  // -------------------------------------------------------------
  private buildSectionBanner(numberStr: string, title: string): Content {
    // pdfmake's runtime reads `id` off any node for page-break introspection
    // (see `pageBreakBefore` above), even though the authoring types only
    // declare `id` on ContentAnchor — hence the explicit intersection below
    // instead of a blind cast.
    const margin: [number, number, number, number] = [0, 6, 0, 4];
    const banner: ContentTable & { id: string } = {
      id: 'sectionBanner',
      table: {
        widths: ['5%', '95%'],
        body: [
          [
            { text: numberStr, style: 'bannerNumberBox' },
            { text: `  ${title}`, style: 'bannerTitleBox' }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#374151',
        vLineColor: () => '#374151',
        paddingLeft: () => 3,
        paddingRight: () => 3,
        paddingTop: () => 2,
        paddingBottom: () => 2
      },
      margin
    };
    return banner;
  }

  private buildSubHeaderBanner(title: string): Content {
    return {
      table: {
        widths: ['*'],
        body: [
          [{ text: title, style: 'subBannerText' }]
        ]
      },
      layout: this.getSubHeaderTableLayout(),
      margin: [0, 0, 0, 2]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 01 - IDENTIFICAÇÃO
  // -------------------------------------------------------------
  private buildIdentificationGrid(report: any, equipment: any, inspection: any, clientName: string): Content {
    const activeClient = clientName || equipment.clientName || '-';
    const tag = equipment.tag || '-';
    const rawProjName = equipment.projectName || (inspection as any)?.projectName;
    const projectName = (rawProjName && rawProjName !== 'NÃO INFORMADO' && rawProjName !== 'undefined') ? rawProjName : '-';
    const unitArea = equipment.unitAreaName || equipment.area || equipment.location || '-';
    const eqType = equipment.equipmentTypeDisplay || equipment.equipmentType || '-';
    const modality = inspection.modality || '-';
    const nature = inspection.nature || '-';
    
    // Piping specifics
    const isPiping = equipment.equipmentType === 'PIPING_SYSTEM' || equipment.equipmentType === 'PIPING_LINE';
    const fromLine = equipment.fromLocation || '-';
    const toLine = equipment.toLocation || '-';

    const baseGrid = {
      table: {
        widths: ['15%', '35%', '15%', '35%'],
        body: [
          [
            { text: 'CLIENTE', style: 'tblLabelCell' },
            { text: activeClient, style: 'tblValueCell' },
            { text: 'PROJETO', style: 'tblLabelCell' },
            { text: projectName, style: 'tblValueCell' }
          ],
          [
            { text: 'TAG DO ATIVO', style: 'tblLabelCell' },
            { text: tag, style: 'tblValueCell', bold: true, fillColor: '#F9FAFB' },
            { text: 'UNIDADE / ÁREA', style: 'tblLabelCell' },
            { text: unitArea, style: 'tblValueCell' }
          ]
        ] as any[][]
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 5] as [number, number, number, number]
    };
    
    if (isPiping) {
    }

    baseGrid.table.body.push([
      { text: 'TIPO DO ATIVO', style: 'tblLabelCell' },
      { text: eqType, style: 'tblValueCell' },
      { text: 'MODALIDADE', style: 'tblLabelCell' },
      { text: modality, style: 'tblValueCell' }
    ] as any[]);



    return baseGrid;
  }

  // -------------------------------------------------------------
  // SEÇÃO 02 - OBJETIVO
  // -------------------------------------------------------------
  private buildObjectiveBlock(objectiveText: string | null): Content {
    const defaultObj = 'Avaliar as condições físicas e operacionais para garantir a conformidade com as normas vigentes e a segurança do processo.';
    const text = objectiveText && objectiveText.trim().length > 0 && objectiveText !== '-'
      ? objectiveText.trim()
      : defaultObj;

    return {
      table: {
        widths: ['*'],
        body: [
          [{ text, style: 'bodyTextBlock' }]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 03 - DOCUMENTOS DE REFERÊNCIA
  // -------------------------------------------------------------
  private buildReferenceDocsTable(docsList: any[]): Content {
    const body: TableCell[][] = [
      [
        { text: 'TIPO', style: 'tblHeaderCell' },
        { text: 'DOCUMENTO', style: 'tblHeaderCell' },
        { text: 'DESCRIÇÃO / ORIGEM', style: 'tblHeaderCell' }
      ]
    ];

    if (docsList && docsList.length > 0) {
      docsList.forEach(item => {
        body.push([
          { text: item.type || item.tipo || 'NORMA', style: 'tblDataCellCenter' },
          { text: item.name || item.doc || item.documento || '-', style: 'tblDataCell', bold: true },
          { text: item.description || item.desc || item.descricao || '-', style: 'tblDataCell' }
        ]);
      });
    } else {
      body.push([
        { text: '-', style: 'tblDataCellCenter' },
        { text: 'Nenhum documento de referência cadastrado.', style: 'tblDataCell' },
        { text: '-', style: 'tblDataCellCenter' }
      ]);
    }

    return {
      table: {
        widths: ['20%', '35%', '45%'],
        body
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 04 - DADOS DE PROJETO
  // -------------------------------------------------------------
  private buildProjectDataGrid(equipment: any): Content {
    const isPiping = equipment.equipmentType === 'PIPING_SYSTEM' || equipment.equipmentType === 'PIPING_LINE' || equipment.equipmentType === 'PIPING';

    if (isPiping) {
      const spec = equipment.properties?.spec || equipment.spec || null;
      const pressureClass = equipment.properties?.pressureClass || equipment.pressureClass || null;
      const materialFamily = equipment.properties?.materialFamily || equipment.materialFamily || '-';
      const materialSpec = equipment.properties?.materialSpecification ? `${equipment.properties.materialSpecification} ${equipment.properties.materialGrade || ''}`.trim() : '-';
      const thickness = equipment.nominalThickness === 'NÃO INFORMADO' ? '-' : equipment.nominalThickness;

      return {
        table: {
          widths: ['16%', '17%', '16%', '17%', '17%', '17%'],
          body: [
            [
              { text: 'PRESSÃO DE PROJETO', style: 'tblLabelCell' },
              { text: equipment.designPressure || '-', style: 'tblValueCell' },
              { text: 'TEMPERATURA DE PROJETO', style: 'tblLabelCell' },
              { text: equipment.designTemperature || '-', style: 'tblValueCell' },
              { text: 'PMTA', style: 'tblLabelCell' },
              { text: equipment.pmta || '-', style: 'tblValueCell', bold: true }
            ],
            [
              { text: 'CÓDIGO DE PROJETO', style: 'tblLabelCell' },
              { text: equipment.projectCode || '-', style: 'tblValueCell' },
              { text: 'NORMA DE FABRICAÇÃO', style: 'tblLabelCell' },
              { text: equipment.fabricationStandard || '-', style: 'tblValueCell' },
              { text: 'CLASSE DE PRESSÃO', style: 'tblLabelCell' },
              { text: pressureClass ? `${pressureClass}#` : '-', style: 'tblValueCell' }
            ],
            [
              { text: 'FAMÍLIA DE MATERIAL', style: 'tblLabelCell' },
              { text: materialFamily, style: 'tblValueCell', colSpan: 2 },
              {},
              { text: 'ESPECIFICAÇÃO / GRAU', style: 'tblLabelCell' },
              { text: materialSpec, style: 'tblValueCell', colSpan: 2 },
              {}
            ],
            [
              { text: 'ESPESSURA NOMINAL (mm)', style: 'tblLabelCell' },
              { text: thickness, style: 'tblValueCell' },
              { text: 'SOBRE-ESPESSURA DE CORROSÃO (mm)', style: 'tblLabelCell' },
              { text: equipment.corrosionAllowance || '-', style: 'tblValueCell' },
              { text: 'ANO DE FABRICAÇÃO / FABRICANTE', style: 'tblLabelCell' },
              { text: `${equipment.manufacturingYear || '-'} / ${equipment.manufacturer || '-'}`, style: 'tblValueCell' }
            ],
            [
              { text: 'REVESTIMENTO INTERNO', style: 'tblLabelCell' },
              { text: equipment.internalCoating && equipment.internalCoating !== '—' ? equipment.internalCoating : '—', style: 'tblValueCell', colSpan: 2 },
              {},
              { text: 'REVESTIMENTO EXTERNO', style: 'tblLabelCell' },
              { text: equipment.externalCoating && equipment.externalCoating !== '—' ? equipment.externalCoating : '—', style: 'tblValueCell', colSpan: 2 },
              {}
            ],
            [
              { text: 'DESENHO DE PROJETO', style: 'tblLabelCell' },
              { text: equipment.projectDrawingFlowchart || '-', style: 'tblValueCell', colSpan: 5 },
              {}, {}, {}, {}
            ]
          ]
        },
        layout: this.getStandardTableLayout(),
        margin: [0, 0, 0, 4]
      };
    }

    return {
      table: {
        widths: ['16%', '17%', '16%', '17%', '17%', '17%'],
        body: [
          [
            { text: 'PRESSÃO DE PROJETO', style: 'tblLabelCell' },
            { text: equipment.designPressure || '-', style: 'tblValueCell' },
            { text: 'TEMPERATURA DE PROJETO', style: 'tblLabelCell' },
            { text: equipment.designTemperature || '-', style: 'tblValueCell' },
            { text: 'PMTA', style: 'tblLabelCell' },
            { text: equipment.pmta || '-', style: 'tblValueCell', bold: true }
          ],
          [
            { text: 'ANO DE FABRICAÇÃO', style: 'tblLabelCell' },
            { text: equipment.manufacturingYear || '-', style: 'tblValueCell' },
            { text: 'MATERIAL', style: 'tblLabelCell' },
            { text: equipment.material || '-', style: 'tblValueCell' },
            { text: 'SOBRE-ESPESSURA DE CORROSÃO (mm)', style: 'tblLabelCell' },
            { text: equipment.corrosionAllowance || '-', style: 'tblValueCell' }
          ],
          [
            { text: 'ESPESSURA NOMINAL (mm)', style: 'tblLabelCell' },
            { text: equipment.nominalThickness || '-', style: 'tblValueCell' },
            { text: 'CÓDIGO DE PROJETO', style: 'tblLabelCell' },
            { text: equipment.projectCode || '-', style: 'tblValueCell' },
            { text: 'NORMA DE FABRICAÇÃO', style: 'tblLabelCell' },
            { text: equipment.fabricationStandard || '-', style: 'tblValueCell' }
          ],
          [
            { text: 'FABRICANTE', style: 'tblLabelCell' },
            { text: equipment.manufacturer || '-', style: 'tblValueCell', colSpan: 5 },
            {}, {}, {}, {}
          ],
          [
            { text: 'REVESTIMENTO INTERNO', style: 'tblLabelCell' },
            { text: equipment.internalCoating && equipment.internalCoating !== '—' ? equipment.internalCoating : '—', style: 'tblValueCell', colSpan: 2 },
            {},
            { text: 'REVESTIMENTO EXTERNO', style: 'tblLabelCell' },
            { text: equipment.externalCoating && equipment.externalCoating !== '—' ? equipment.externalCoating : '—', style: 'tblValueCell', colSpan: 2 },
            {}
          ],
          [
            { text: 'DESENHO DE PROJETO / FLUXOGRAMA', style: 'tblLabelCell' },
            { text: equipment.projectDrawingFlowchart && equipment.projectDrawingFlowchart !== 'NÃO INFORMADO' ? equipment.projectDrawingFlowchart : '-', style: 'tblValueCell', colSpan: 5 },
            {}, {}, {}, {}
          ]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 05 - DADOS DE OPERAÇÃO
  // -------------------------------------------------------------
  
  private formatDeviceType(type: string): string {
    if (!type || type === '-') return '-';
    const t = type.toUpperCase().trim();
    if (t === 'PSV' || t === 'SAFETY_VALVE' || t === 'SAFETY_RELIEF_VALVE') return 'PSV';
    if (t === 'PRV' || t === 'RELIEF_VALVE') return 'PRV';
    if (t === 'RUPTURE_DISC' || t === 'BURSTING_DISC') return 'DISCO DE RUPTURA';
    if (t === 'VACUUM_VALVE') return 'VÁLVULA DE VÁCUO';
    if (t === 'FLAME_ARRESTER') return 'CORTA-CHAMAS';
    return t.replace(/_/g, ' ');
  }

  private formatInstrumentType(type: string): string {
    if (!type || type === '-') return '-';
    const t = type.toUpperCase().trim();
    if (t === 'PRESSURE_GAUGE' || t === 'MANOMETER' || t === 'MANOMETRO') return 'MANÔMETRO (PI)';
    if (t === 'PRESSURE_TRANSMITTER') return 'TRANSMISSOR DE PRESSÃO (PT)';
    if (t === 'THERMOMETER' || t === 'TERMOMETRO') return 'TERMÔMETRO (TI)';
    if (t === 'TEMPERATURE_TRANSMITTER') return 'TRANSMISSOR DE TEMP. (TT)';
    if (t === 'LEVEL_GAUGE' || t === 'LEVEL_INDICATOR') return 'INDICADOR DE NÍVEL (LG)';
    if (t === 'LEVEL_TRANSMITTER') return 'TRANSMISSOR DE NÍVEL (LT)';
    if (t === 'FLOW_METER') return 'MEDIDOR DE VAZÃO (FI)';
    if (t === 'SAFETY_SWITCH') return 'CHAVE DE SEGURANÇA (PS)';
    return t.replace(/_/g, ' ');
  }

  // -------------------------------------------------------------
  // DISPOSITIVOS DE SEGURANÇA E INSTRUMENTOS (ITEM 06)
  // -------------------------------------------------------------
  private buildSafetyDevicesAndInstrumentsGrid(safetyDevices: any[], instruments: any[]): any {
    const content = [];

    if (safetyDevices && safetyDevices.length > 0) {
      content.push({
        text: 'DISPOSITIVOS DE SEGURANÇA (PSV / PRV / DISCO DE RUPTURA)',
        style: 'tblHeaderCell',
        fillColor: '#E5E7EB',
        color: '#111827',
        alignment: 'left',
        margin: [0, 4, 0, 4]
      });

      const body: any[][] = [
        [
          { text: 'TAG / IDENTIFICAÇÃO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'left' },
          { text: 'TIPO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'PRESSÃO ABERTURA', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'CERTIFICADO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'VALIDADE', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'STATUS', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' }
        ]
      ];

      for (const dev of safetyDevices) {
        body.push([
          { text: (dev.tag || dev.name || '-').toUpperCase(), style: 'tableBody', alignment: 'left' },
          { text: this.formatDeviceType(dev.deviceType || '-'), style: 'tableBody', alignment: 'center' },
          { text: dev.setPressure ? `${dev.setPressure} ${dev.setPressureUnit || 'kgf/cm²'}` : '-', style: 'tableBody', alignment: 'center' },
          { text: (dev.calibrationCertificate || dev.certificateNumber || dev.testCertificate || '-').toUpperCase(), style: 'tableBody', alignment: 'center' },
          { text: dev.nextTestDate ? new Date(dev.nextTestDate).toLocaleDateString('pt-BR') : (dev.calibrationDueDate ? new Date(dev.calibrationDueDate).toLocaleDateString('pt-BR') : '-'), style: 'tableBody', alignment: 'center' },
          { text: (dev.status === 'ACTIVE' ? 'ATIVO' : dev.status === 'INACTIVE' ? 'INATIVO' : dev.status === 'TEST_DUE' ? 'VENCIDO' : dev.status || '-').toUpperCase(), style: 'tableBody', alignment: 'center' }
        ]);
      }

      content.push({
        table: {
          headerRows: 1,
          widths: ['24%', '15%', '17%', '18%', '13%', '13%'],
          body: body
        },
        layout: this.getStandardTableLayout(),
        margin: [0, 0, 0, 8]
      });
    }

    if (instruments && instruments.length > 0) {
      content.push({
        text: 'INSTRUMENTOS DE SEGURANÇA E CONTROLE',
        style: 'tblHeaderCell',
        fillColor: '#E5E7EB',
        color: '#111827',
        alignment: 'left',
        margin: [0, 4, 0, 4]
      });

      const body: any[][] = [
        [
          { text: 'TAG / IDENTIFICAÇÃO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'left' },
          { text: 'TIPO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'FAIXA DE TRABALHO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'CERTIFICADO', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'VALIDADE', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' },
          { text: 'STATUS', style: 'tblHeaderCell', fillColor: '#374151', color: '#FFFFFF', alignment: 'center' }
        ]
      ];

      for (const inst of instruments) {
        let rangeStr = '-';
        if (inst.rangeMin !== undefined && inst.rangeMax !== undefined && inst.rangeMin !== null && inst.rangeMax !== null) {
          rangeStr = `${inst.rangeMin} a ${inst.rangeMax} ${inst.unit || ''}`.trim();
        }

        body.push([
          { text: (inst.tag || inst.name || '-').toUpperCase(), style: 'tableBody', alignment: 'left' },
          { text: this.formatInstrumentType(inst.instrumentType || inst.type || '-'), style: 'tableBody', alignment: 'center' },
          { text: rangeStr, style: 'tableBody', alignment: 'center' },
          { text: (inst.calibrationCertificate || inst.certificateNumber || '-').toUpperCase(), style: 'tableBody', alignment: 'center' },
          { text: inst.calibrationDueDate ? new Date(inst.calibrationDueDate).toLocaleDateString('pt-BR') : '-', style: 'tableBody', alignment: 'center' },
          { text: (inst.status === 'ACTIVE' ? 'ATIVO' : inst.status === 'INACTIVE' ? 'INATIVO' : inst.status === 'CALIBRATION_DUE' ? 'VENCIDO' : inst.status || '-').toUpperCase(), style: 'tableBody', alignment: 'center' }
        ]);
      }

      content.push({
        table: {
          headerRows: 1,
          widths: ['24%', '18%', '16%', '16%', '13%', '13%'],
          body: body
        },
        layout: this.getStandardTableLayout(),
        margin: [0, 0, 0, 8]
      });
    }

    if (content.length === 0) {
      content.push({
        text: 'Nenhum dispositivo de segurança ou instrumento vinculado ao equipamento.',
        style: 'tableBody',
        italics: true,
        margin: [0, 4, 0, 8]
      });
    }

    return {
      stack: content,
      margin: [0, 0, 0, 8]
    };
  }

  /**
   * "DD/MM/AAAA a DD/MM/AAAA" — o período em que a inspeção foi executada.
   *
   * Com apenas uma das datas, imprime a que existe em vez de inventar a
   * outra ou omitir o campo inteiro: saber metade do período é melhor que
   * não saber nada, e a lacuna fica visível.
   *
   * As datas chegam já formatadas pelo mapper, que usa 'NÃO INFORMADO' no
   * lugar de string vazia quando a inspeção não tem a data. Sem reconhecer
   * esse marcador, o cabeçalho sairia com "NÃO INFORMADO a NÃO INFORMADO".
   * O mapper também copia a data inicial para a final quando só existe uma —
   * daí a igualdade imprimir uma data só, e não "X a X".
   */
  private formatInspectionPeriod(inspection: any): string {
    const clean = (value: unknown): string => {
      const text = String(value ?? '').trim();
      return text === '' || text === '-' || text === '—' || text.toUpperCase() === 'NÃO INFORMADO' ? '' : text;
    };
    const start = clean(inspection?.startDate);
    const end = clean(inspection?.endDate);
    if (start && end) return start === end ? start : `${start} a ${end}`;
    if (start) return `A partir de ${start}`;
    if (end) return `Até ${end}`;
    return '—';
  }

  private buildOperationDataGrid(equipment: any): Content {
    return {
      table: {
        widths: ['16%', '34%', '16%', '34%'],
        body: [
          [
            { text: 'PRESSÃO OPER.', style: 'tblLabelCell' },
            { text: equipment.operatingPressure || '-', style: 'tblValueCell' },
            { text: 'TEMP. OPER.', style: 'tblLabelCell' },
            { text: equipment.operatingTemperature || '-', style: 'tblValueCell' }
          ],
          [
            { text: 'SERVIÇO / FLUIDO', style: 'tblLabelCell' },
            { text: equipment.service || '-', style: 'tblValueCell' },
            { text: 'CLASSE FLUIDO', style: 'tblLabelCell' },
            { text: equipment.fluidClass || '-', style: 'tblValueCell' }
          ],
          [
            /**
             * CATEGORIA — vem do cadastro técnico do ativo (Snapshot), nunca
             * fixa no código. NÃO é a Classe de Fluido: a classe descreve o
             * produto transportado (A–D) e a categoria é o enquadramento do
             * equipamento (NR-13, I–V). Mostrá-las lado a lado deixa a
             * diferença visível e impede a troca de uma pela outra.
             */
            { text: 'CATEGORIA', style: 'tblLabelCell' },
            { text: equipment.category || equipment.nr13Category || '-', style: 'tblValueCell' },
            { text: '', style: 'tblLabelCell' },
            { text: '', style: 'tblValueCell' }
          ]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  /**
   * LINHAS QUE COMPOE O SISTEMA — comando "ajuste da Lista de Linhas", Parte
   * B: exclusivamente TAG DA LINHA, DE, PARA, TEMPERATURA DE OPERAÇÃO e
   * PRESSÃO DE OPERAÇÃO — nenhum outro dado técnico (fluido, diâmetro, SCH,
   * spec, material, desenho, fluxograma, pressão/temperatura de projeto,
   * PMTA, espessuras, classes...). Esses dados permanecem só na Lista
   * completa do Book (item 11/19 do comando anterior, reafirmado no item 19
   * deste). Sem coluna "Nº": a primeira coluna é TAG DA LINHA (item 17). A
   * fonte é a mesma (GeneratedLineList) do Book — só a apresentação difere.
   *
   * TAG é identificador obrigatório: uma Linha vinculada sem TAG válida no
   * cadastro (`tag === NÃO INFORMADO`) não entra como registro normal — é
   * uma inconsistência cadastral, sinalizada à parte para correção.
   */
  private buildLineListSection(list: GeneratedLineList): Content[] {
    const total: Content = { text: `TOTAL DE LINHAS DO SISTEMA: ${list.totalLines}`, style: 'tblLabelCell', margin: [0, 0, 0, 6] };
    if (list.totalLines === 0) {
      return [total, { text: 'Nenhuma linha vinculada ao sistema.', style: 'bodyTextBlock' }];
    }
    /**
     * `lines` ausente num snapshot antigo não pode derrubar a geração do RI
     * inteiro — mas também não pode virar uma seção vazia que passa por
     * "sistema sem linhas". O total declarado continua impresso e a
     * divergência é denunciada no próprio documento.
     */
    const lines = Array.isArray(list.lines) ? list.lines : [];
    if (lines.length === 0) {
      return [total, {
        text: `INCONSISTÊNCIA: o Sistema declara ${list.totalLines} linha(s), mas o detalhamento não veio neste registro. Regere a Lista de Linhas antes de emitir este Relatório.`,
        style: 'docNoticeFootnote', margin: [0, 6, 0, 0]
      }];
    }
    const validLines = lines.filter(line => line.tag !== NOT_INFORMED);
    const invalidCount = lines.length - validLines.length;
    const content: Content[] = [total];
    if (validLines.length > 0) {
      content.push(buildRiLineListTable(validLines, { header: 'tblHeaderCell', body: 'tblDataCell' }));
    }
    if (invalidCount > 0) {
      content.push({
        text: `INCONSISTÊNCIA CADASTRAL: ${invalidCount} linha(s) vinculada(s) ao Sistema sem TAG válida no cadastro. Corrija o cadastro da Tubulação antes de emitir este Relatório.`,
        style: 'docNoticeFootnote', margin: [0, 6, 0, 0]
      });
    }
    return content;
  }

  // -------------------------------------------------------------
  // SEÇÃO 06 - INSPEÇÃO REALIZADA
  // -------------------------------------------------------------
  private buildInspectionRealizedGrid(inspection: any): Content {
    return {
      table: {
        widths: ['16%', '18%', '16%', '18%', '16%', '16%'],
        body: [
          [
            { text: 'PERÍODO INICIAL', style: 'tblLabelCell' },
            { text: inspection.startDate || '-', style: 'tblValueCell' },
            { text: 'PERÍODO FINAL', style: 'tblLabelCell' },
            { text: inspection.endDate || '-', style: 'tblValueCell', colSpan: 3 },
            {},
            {}
          ],
          [
            { text: 'CONDIÇÃO', style: 'tblLabelCell' },
            { text: inspection.operationalCondition || '-', style: 'tblValueCell' },
            { text: 'NATUREZA', style: 'tblLabelCell' },
            { text: inspection.nature || '-', style: 'tblValueCell' },
            { text: 'MODALIDADE', style: 'tblLabelCell' },
            { text: inspection.modality || '-', style: 'tblValueCell' }
          ]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 3]
    };
  }

  private buildInspectionObservationsBlock(obsText: string | null): Content {
    const text = obsText && obsText.trim().length > 0 && obsText !== '-'
      ? obsText
      : '-';

    return {
      table: {
        widths: ['*'],
        body: [
          [{ text, style: 'bodyTextBlock' }]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 07 - INSPEÇÃO VISUAL POR COMPONENTE
  // -------------------------------------------------------------
  private buildVisualInspectionTable(checklistComponents: any[]): Content {
    const body: TableCell[][] = [
      [
        { text: 'COMPONENTE', style: 'tblHeaderCell' },
        { text: 'STATUS', style: 'tblHeaderCell' },
        { text: 'COMENTÁRIO TÉCNICO', style: 'tblHeaderCell' },
        { text: 'REC.', style: 'tblHeaderCell' },
        { text: 'EVIDÊNCIA', style: 'tblHeaderCell' }
      ]
    ];

    if (checklistComponents && checklistComponents.length > 0) {
      checklistComponents.forEach(comp => {
        body.push([
          { text: comp.definitionComponentId || comp.name || comp.groupId || 'Componente', style: 'tblDataCell', bold: true },
          {
            text: comp.resultDisplay || comp.result || 'APROVADO',
            style: 'tblDataCellCenter',
            bold: true,
            color: comp.result === 'REJECTED' || comp.result === 'REPROVADO' ? '#000000' : '#111827',
            fillColor: comp.result === 'REJECTED' || comp.result === 'REPROVADO' ? '#D1D5DB' : '#F3F4F6'
          },
          { text: (comp.inspectionNotes || comp.technicalComment || comp.description || '').trim() || '—', style: 'tblDataCell' },
          { text: comp.recommendationCode || '-', style: 'tblDataCellCenter' },
          { text: comp.evidence || '-', style: 'tblDataCellCenter' }
        ]);
      });
    } else {
      body.push([
        { text: '-', style: 'tblDataCellCenter' },
        { text: 'Nenhum componente inspecionado registrado no checklist.', style: 'tblDataCell', colSpan: 4 },
        {},
        {},
        {}
      ]);
    }

    return {
      table: {
        widths: ['25%', '18%', '35%', '11%', '11%'],
        body
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 08 - ENSAIOS NÃO DESTRUTIVOS EXECUTADOS
  // -------------------------------------------------------------
  private buildNdtTable(performedMethods: any[]): Content {
    const body: TableCell[][] = [
      [
        { text: 'MÉTODO / ENSAIO', style: 'tblHeaderCell' },
        { text: 'RELATÓRIO', style: 'tblHeaderCell' },
        { text: 'DATA', style: 'tblHeaderCell' },
        { text: 'RESULTADO / PARECER', style: 'tblHeaderCell' },
        { text: 'OBSERVAÇÕES', style: 'tblHeaderCell' }
      ]
    ];

    if (performedMethods && performedMethods.length > 0) {
      performedMethods.forEach(item => {
        body.push([
          { text: item.methodName || item.method || 'ME', style: 'tblDataCellCenter', bold: true },
          { text: item.reportNumber || '-', style: 'tblDataCellCenter' },
          { text: item.date || '-', style: 'tblDataCellCenter' },
          { text: item.resultDisplay || item.result || 'APROVADO', style: 'tblDataCellCenter' },
          { text: item.notes || '-', style: 'tblDataCell' }
        ]);
      });
    } else {
      body.push([
        { text: '-', style: 'tblDataCellCenter' },
        { text: 'Nenhum ensaio não destrutivo adicional executado / vinculado.', style: 'tblDataCell', colSpan: 4 },
        {},
        {},
        {}
      ]);
    }

    return {
      table: {
        widths: ['25%', '20%', '15%', '20%', '20%'],
        body
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 09 - SERVIÇOS DE MANUTENÇÃO REALIZADOS
  // -------------------------------------------------------------
  private buildMaintenanceTable(maintenanceServicesText: string | null): Content {
    const hasText = maintenanceServicesText && maintenanceServicesText.trim().length > 0 && maintenanceServicesText !== '-';

    const textToDisplay = hasText
      ? maintenanceServicesText.trim()
      : 'Não foi realizado serviços de manutenção durante essa inspeção.';

    const body: TableCell[][] = [
      [
        { text: 'DESCRIÇÃO DOS SERVIÇOS DE MANUTENÇÃO REALIZADOS', style: 'tblHeaderCell' }
      ],
      [
        { text: textToDisplay, style: 'tblDataCell' }
      ]
    ];

    return {
      table: {
        widths: ['100%'],
        body
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 10 - RECOMENDAÇÕES DE INSPEÇÃO
  // -------------------------------------------------------------
  private buildRecommendationsTable(recommendations: any[]): Content {
    const body: TableCell[][] = [
      [
        { text: 'Nº REC', style: 'tblHeaderCell' },
        { text: 'CRITICIDADE', style: 'tblHeaderCell' },
        { text: 'DISCIPLINA', style: 'tblHeaderCell' },
        { text: 'TIPO DE SERVIÇO', style: 'tblHeaderCell' },
        { text: 'Nº ZR', style: 'tblHeaderCell' },
        { text: 'RECOMENDAÇÃO FINAL', style: 'tblHeaderCell' }
      ]
    ];

    if (recommendations && recommendations.length > 0) {
      recommendations.forEach(r => {
        const crit = r.criticality || 'MODERATE';
        const isCritical = crit === 'CRITICAL' || crit === 'HIGH';

        body.push([
          { text: r.code || r.id, style: 'tblDataCellCenter', bold: true },
          {
            text: r.criticalityDisplay || crit,
            style: 'tblDataCellCenter',
            bold: isCritical,
            color: isCritical ? '#000000' : '#111827',
            fillColor: isCritical ? '#D1D5DB' : (crit === 'MODERATE' ? '#E5E7EB' : '#F3F4F6')
          },
          { text: r.discipline || 'MECÂNICA', style: 'tblDataCellCenter' },
          { text: r.executeInDisplay || (r.executeIn === 'SHUTDOWN' ? 'Parada' : 'Rotina'), style: 'tblDataCellCenter' },
          { text: (r.zr && r.zr !== 'undefined') ? r.zr : '-', style: 'tblDataCellCenter' },
          { text: r.scope || '-', style: 'tblDataCell' }
        ]);
      });
    } else {
      body.push([
        { text: '-', style: 'tblDataCellCenter' },
        { text: 'Nenhuma recomendação de inspeção emitida para este relatório.', style: 'tblDataCell', colSpan: 5 },
        {},
        {},
        {},
        {}
      ]);
    }

    return {
      table: {
        widths: ['18%', '14%', '14%', '14%', '10%', '30%'],
        body
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 11 - DATAS LIMITES PARA PRÓXIMAS INSPEÇÕES
  // -------------------------------------------------------------
  private buildNextInspectionDatesTable(nextDatesText: string | null): Content {
    const body: TableCell[][] = [
      [
        { text: 'TIPO DE INSPEÇÃO', style: 'tblHeaderCell' },
        { text: 'DATA LIMITE PRÓXIMA INSPEÇÃO', style: 'tblHeaderCell' },
        { text: 'REFERÊNCIA REGULAMENTAR', style: 'tblHeaderCell' }
      ]
    ];

    if (nextDatesText && nextDatesText.trim().length > 0 && nextDatesText !== '-') {
      body.push([
        { text: 'Inspeção de Integridade NR-13', style: 'tblDataCell', bold: true },
        { text: nextDatesText, style: 'tblDataCellCenter', bold: true, color: '#111827' },
        { text: 'Portaria MTP 1.847/2022 (NR-13)', style: 'tblDataCell' }
      ]);
    } else {
      body.push([
        { text: 'Inspeção de Integridade NR-13', style: 'tblDataCell', bold: true },
        { text: 'A definir pelo Profissional Habilitado (PLH)', style: 'tblDataCellCenter', bold: true, color: '#111827' },
        { text: 'Portaria MTP 1.847/2022 (NR-13)', style: 'tblDataCell' }
      ]);
    }

    return {
      table: {
        widths: ['35%', '35%', '30%'],
        body
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 12 - DISCUSSÃO SOBRE MECANISMOS DE DANO
  // -------------------------------------------------------------
  private buildDamageMechanismsBlock(mechanismsText: string | null): Content {
    const text = mechanismsText && mechanismsText.trim().length > 0 && mechanismsText !== '-'
      ? mechanismsText
      : 'Não foram relatados mecanismos de dano específicos para este equipamento nesta inspeção.';

    return {
      table: {
        widths: ['*'],
        body: [
          [{ text, style: 'bodyTextBlock' }]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 4]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 13/14 - CONCLUSÃO
  // -------------------------------------------------------------
  private buildConclusionGrid(report: any): Content {
    // P0-07/08/09: Ler engineeringAssessment diretamente quando campos mapeados
    // não existem no objeto do relatório (fallback para domínio real).
    const assessment = report.engineeringAssessment;
    const rawCondition = report.condition || assessment?.equipmentCondition;
    let condicaoFinal = 'EM AVALIAÇÃO';
    if (rawCondition) {
      const clean = String(rawCondition).toUpperCase().trim();
      if (clean === 'APTO' || clean === 'APPROVED') condicaoFinal = 'APTO';
      else if (clean === 'NAO_APTO' || clean === 'NÃO APTO' || clean === 'REJECTED' || clean === 'REPROVADO') condicaoFinal = 'NÃO APTO';
      else if (clean === 'CRITERIO_CLIENTE' || clean === 'CRITÉRIO DO CLIENTE' || clean === 'A CRITÉRIO DO CLIENTE') condicaoFinal = 'A CRITÉRIO DO CLIENTE';
      else condicaoFinal = rawCondition;
    }
    const conclusaoTecnica = report.conclusion && report.conclusion.trim().length > 0 && report.conclusion !== '-' && report.conclusion !== 'NÃO INFORMADO'
      ? report.conclusion.trim()
      : 'NÃO INFORMADO';
    const restricoes = report.restrictions && report.restrictions.trim().length > 0 && report.restrictions !== '-'
      ? report.restrictions.trim()
      : 'Nenhuma restrição imposta à operação regular do equipamento.';
    // Vida residual: usar campo mapeado OU derivar de assessment.residualLife.adoptedMonths
    let vidaResidual = report.residualLifeAdopted;
    if (!vidaResidual && assessment?.residualLife?.adoptedMonths) {
      const months = assessment.residualLife.adoptedMonths;
      const yearsVal = (months / 12).toFixed(1).replace('.', ',');
      vidaResidual = `${yearsVal} anos (${months} meses)`;
    }
    if (!vidaResidual) vidaResidual = 'NÃO INFORMADA';
    // Período de aptidão: usar campo mapeado OU derivar de adoptedMonths * 30.4375
    let periodoAptidao = report.aptPeriodDays;
    if (!periodoAptidao && assessment?.residualLife?.adoptedMonths) {
      const totalDays = Math.round(assessment.residualLife.adoptedMonths * 30.4375);
      periodoAptidao = `${totalDays.toLocaleString('pt-BR')} dias`;
    }
    if (!periodoAptidao) periodoAptidao = 'NÃO INFORMADO';

    const isInapto = condicaoFinal === 'INAPTO' || condicaoFinal === 'NÃO APTO' || condicaoFinal === 'NAO_APTO';

    return {
      table: {
        widths: ['24%', '76%'],
        body: [
          [
            { text: 'CONDIÇÃO FINAL', style: 'tblLabelCell' },
            {
              text: condicaoFinal,
              style: 'tblValueCell',
              bold: true,
              color: isInapto ? '#000000' : '#111827',
              fillColor: isInapto ? '#D1D5DB' : '#F9FAFB'
            }
          ],
          [
            { text: 'VIDA RESIDUAL ADOTADA', style: 'tblLabelCell' },
            { text: vidaResidual, style: 'tblValueCell', bold: true }
          ],
          [
            { text: 'PERÍODO DE APTIDÃO', style: 'tblLabelCell' },
            { text: periodoAptidao, style: 'tblValueCell' }
          ],
          [
            { text: 'CONCLUSÃO TÉCNICA', style: 'tblLabelCell' },
            { text: conclusaoTecnica, style: 'tblValueCell' }
          ],
          [
            { text: 'RESTRIÇÕES / CONDIÇÕES', style: 'tblLabelCell' },
            { text: restricoes, style: 'tblValueCell' }
          ]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 6]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 14/15 - ANEXO FOTOGRÁFICO
  // -------------------------------------------------------------
  private buildPhotosPages(photos: any[]): Content[] {
    if (!photos || photos.length === 0) {
      return [
        {
          table: {
            widths: ['*'],
            body: [
              [{ text: 'Nenhum registro fotográfico anexado à inspeção.', style: 'bodyTextBlock', italics: true }]
            ]
          },
          layout: this.getStandardTableLayout(),
          margin: [0, 0, 0, 4]
        }
      ];
    }

    const blocks: Content[] = [];
    photos.forEach((p, idx) => {
      blocks.push(this.buildSinglePhotoBox(idx + 1, p.fileUrl || p.image, p.component || p.title || 'Componente Inspecionado', p.comment || p.description || 'Registro fotográfico de evidência técnica.'));
    });

    return blocks;
  }

  private buildSinglePhotoBox(index: number, imageSrc: string | null, component: string, comment: string): Content {
    const photoNumStr = `FOTO ${index.toString().padStart(2, '0')}`;

    let leftContent: Content;
    if (imageSrc && imageSrc.startsWith('data:image')) {
      leftContent = {
        image: imageSrc,
        width: 190,
        height: 105,
        alignment: 'center',
        margin: [4, 6, 4, 6]
      };
    } else {
      leftContent = {
        table: {
          widths: ['*'],
          body: [
            [{ text: '\n\nÁrea reservada para fotografia\n\n', alignment: 'center', style: 'docNoticeFootnote' }]
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#E2E8F0',
          vLineColor: () => '#E2E8F0',
          fillColor: () => '#F8FAFC'
        },
        margin: [4, 4, 4, 4]
      };
    }

    const rightContent: Content = {
      stack: [
        {
          text: [
            { text: 'Componente: ', style: 'photoFieldLabel' },
            { text: component, style: 'photoFieldValue' }
          ],
          margin: [4, 6, 4, 8]
        },
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 240, y2: 0, lineWidth: 0.5, lineColor: '#94A3B8' }],
          margin: [4, 0, 4, 8]
        },
        {
          text: [
            { text: 'Comentário:\n', style: 'photoFieldLabel' },
            { text: comment, style: 'photoFieldValue' }
          ],
          margin: [4, 0, 4, 8]
        },
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 240, y2: 0, lineWidth: 0.5, lineColor: '#CBD5E1' }],
          margin: [4, 4, 4, 4]
        }
      ]
    };

    return {
      unbreakable: true,
      table: {
        widths: ['*'],
        body: [
          [{ text: photoNumStr, style: 'photoBoxHeader' }],
          [
            {
              table: {
                widths: ['46%', '54%'],
                body: [
                  [leftContent, rightContent]
                ]
              },
              layout: 'noBorders'
            }
          ]
        ]
      },
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 6]
    };
  }

  // -------------------------------------------------------------
  // SEÇÃO 15/16 - SIGNATÁRIO(S)
  // -------------------------------------------------------------
  private buildSignatoriesTable(signatories: any): Content[] {
    // Handling array or object format
    let inspectorSig, plhSig, fiscalSig;
    if (Array.isArray(signatories)) {
      inspectorSig = signatories.find((s: any) => s.role === 'INSPECTOR');
      plhSig = signatories.find((s: any) => s.role === 'PLH' || s.role === 'ENGINEER');
      fiscalSig = signatories.find((s: any) => s.role === 'FISCAL' || s.role === 'FISCALIZAÇÃO');
    } else if (signatories) {
      inspectorSig = signatories.inspector;
      plhSig = signatories.plh;
      fiscalSig = signatories.fiscal;
    }

    const inspectorName = inspectorSig?.name || '-';
    const inspectorCred = inspectorSig?.cred || '';
    const inspectorImg = inspectorSig?.signatureImage || inspectorSig?.signatureUrl;

    const plhName = plhSig?.name || '-';
    const plhCred = plhSig?.cred || '';
    const plhImg = plhSig?.signatureImage || plhSig?.signatureUrl;

    const fiscalName = fiscalSig?.name || 'FISCALIZAÇÃO';
    const fiscalCred = fiscalSig?.cred || 'FISCAL DO CONTRATO';

    const getSignatureBlock = (imgUrl: string | undefined | null, hasName: boolean, footerLabel: string) => {
      const block: any[] = [];
      if (imgUrl && imgUrl.startsWith('data:image')) {
        block.push({ image: imgUrl, fit: [80, 30], alignment: 'center', margin: [0, -5, 0, 0] });
        block.push({ canvas: [{ type: 'line', x1: 5, y1: 0, x2: 110, y2: 0, lineWidth: 0.5, lineColor: '#94A3B8' }] });
      } else {
        block.push({ text: '\n\n', margin: [0, 0, 0, 15] });
        block.push({ canvas: [{ type: 'line', x1: 5, y1: 0, x2: 110, y2: 0, lineWidth: 0.5, lineColor: '#94A3B8' }] });
      }
      block.push({ text: hasName ? footerLabel : 'Pendente de Designação', style: 'docNoticeFootnote', alignment: 'center', margin: [0, 2, 0, 0] });
      return block;
    };

    const tableBlock = {
      table: {
        widths: ['26%', '24%', '24%', '26%'],
        body: [
          [
            { text: 'Nome', style: 'tblHeaderCell' },
            { text: 'Função', style: 'tblHeaderCell' },
            { text: 'Credencial', style: 'tblHeaderCell' },
            { text: 'Assinatura', style: 'tblHeaderCell' }
          ],
          [
            { stack: [{ text: inspectorName, style: 'tblDataCell', bold: true }], margin: [2, 10, 2, 10] },
            { text: 'INSPETOR', style: 'tblDataCellCenter', bold: true, margin: [2, 10, 2, 10] },
            { stack: [{ text: inspectorCred, style: 'tblDataCell', bold: true }], margin: [2, 10, 2, 10] },
            { stack: getSignatureBlock(inspectorImg, inspectorName !== '-', 'Assinatura Digital / CRT'), margin: [2, 5, 2, 5] }
          ],
          [
            { stack: [{ text: plhName, style: 'tblDataCell', bold: true }], margin: [2, 10, 2, 10] },
            { text: 'ENGENHEIRO PLH', style: 'tblDataCellCenter', bold: true, margin: [2, 10, 2, 10] },
            { stack: [{ text: plhCred, style: 'tblDataCell', bold: true }], margin: [2, 10, 2, 10] },
            { stack: getSignatureBlock(plhImg, plhName !== '-', 'Assinatura Digital / CREA'), margin: [2, 5, 2, 5] }
          ],
          [
            { stack: [{ text: fiscalName, style: 'tblDataCell', bold: true }], margin: [2, 10, 2, 10] },
            { text: 'FISCAL', style: 'tblDataCellCenter', bold: true, margin: [2, 10, 2, 10] },
            { stack: [{ text: fiscalCred, style: 'tblDataCell', bold: true }], margin: [2, 10, 2, 10] },
            { stack: getSignatureBlock(undefined, true, 'Assinatura e Carimbo'), margin: [2, 5, 2, 5] }
          ]
        ]
      },
      dontBreakRows: true,
      layout: this.getStandardTableLayout(),
      margin: [0, 0, 0, 5] as [number, number, number, number]
    };

    return [tableBlock] as any;
  }

  // -------------------------------------------------------------
  // LAYOUTS DE TABELA
  // -------------------------------------------------------------
  private getStandardTableLayout() {
    return {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#CBD5E1',
      vLineColor: () => '#CBD5E1',
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 3,
      paddingBottom: () => 3
    };
  }

  private getSubHeaderTableLayout() {
    return {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#CBD5E1',
      vLineColor: () => '#CBD5E1',
      fillColor: () => '#E2E8F0',
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 2,
      paddingBottom: () => 2
    };
  }
}

documentEngine.registerBuilder("RI", new RiBuilder());
documentEngine.registerBuilder("INTEGRITY", new RiBuilder());
documentEngine.registerBuilder("RELATORIO_INTEGRIDADE", new RiBuilder());
documentEngine.registerBuilder("INTEGRITY_REPORT", new RiBuilder());
documentEngine.registerBuilder("CHECKLIST_VISUAL", new RiBuilder());
